import isDateWithinRange from '@/utils/withinDateRange';
import { vehicleHirePropsProps } from '@/types/types';
import { elapsedDaysCeil } from '@/utils/date/ukFrame';

// This measures the distance between two dates/times
// and returns the number of days, rounded up.
// Export name and path must stay: QuoteForm.tsx imports from here and two
// test files vi.mock this module. Body delegates to elapsedDaysCeil, which
// diffs local fields on a fixed 24h day. The previous treatAsUTC helper
// (getTimezoneOffset shift) drifted ±1 day when the browser's DST transition
// fell mid-rental, because the two dates carried different offsets.
export function daysBetween(startDate: Date, endDate: Date) {
    return elapsedDaysCeil(startDate, endDate);
}

export function calculateVehiclePrice(
    numRentalDays: number,
    collectionDate: Date | null,
    chosenVehicle: vehicleHirePropsProps | null
): { price: number; usedSeasonalPrice?: boolean; used84DayPrice?: boolean } {
    if (!numRentalDays || numRentalDays < 1)
        return { price: 0, usedSeasonalPrice: false };

    const isSummer = isDateWithinRange(collectionDate); // if isSummer is false, then it's the equivalent of isWinter being true

    const round = (value: number) =>
        Math.round((value + Number.EPSILON) * 100) / 100;

    const {
        pricePerDay,
        pricePer2Days,
        pricePer6Days,
        pricePerWeek,
        pricePer4WeeksSummer,
        pricePer4WeeksWinter,
        pricePer84Days,
    } = chosenVehicle || {};
    let calculatedPrice;
    const summerWinterPrice = isSummer
        ? pricePer4WeeksSummer
        : pricePer4WeeksWinter;

    switch (true) {
        case numRentalDays === 1:
            return { price: pricePerDay as number, usedSeasonalPrice: false };
        case numRentalDays > 1 && numRentalDays < 6:
            return {
                price: round(((pricePer2Days as number) / 2) * numRentalDays),
                usedSeasonalPrice: false,
            };
        case numRentalDays === 6:
            return { price: pricePer6Days as number, usedSeasonalPrice: false };
        case numRentalDays === 7:
            return { price: pricePerWeek as number, usedSeasonalPrice: false };
        case numRentalDays > 7 && numRentalDays <= 27:
            calculatedPrice = round(
                ((pricePerWeek as number) / 7) * numRentalDays
            );
            return calculatedPrice > summerWinterPrice!
                ? {
                      price: summerWinterPrice as number,
                      usedSeasonalPrice: true,
                  }
                : { price: calculatedPrice, usedSeasonalPrice: false };
        case numRentalDays > 27 && numRentalDays <= 83:
            calculatedPrice = round(
                ((summerWinterPrice as number) / 28) * numRentalDays
            );
            return calculatedPrice > pricePer84Days!
                ? {
                      price: round(pricePer84Days as number),
                      usedSeasonalPrice: true,
                  }
                : {
                      price: calculatedPrice,
                      usedSeasonalPrice: false,
                  };
        case numRentalDays > 83:
            return {
                price: ((pricePer84Days as number) / 84) * numRentalDays,
                usedSeasonalPrice: false,
            };
        default:
            return { price: 0, usedSeasonalPrice: false };
    }
}
